import TwigBuilder from '../../Library/TwigBuilder.js';
import {fetchJson} from '../../Library/Fetch.js';
import {toggleLoading} from '../../Library/LoadingIndicator';

// Mirror ActionValueRenderer / FilterValueRenderer minus multi-argument
// filters — anything outside these lists fails validation on save.
const ACTION_VALUE_FILTERS = [
    'upper', 'lower', 'capitalize', 'title', 'trim', 'digits', 'length', 'default',
    'date', 'number_format', 'json_encode', 'striptags', 'first', 'last',
    'reverse', 'abs', 'join', 'split',
];
const FILTER_VALUE_FILTERS = [
    'upper', 'lower', 'capitalize', 'title', 'trim', 'digits', 'length', 'default',
    'date', 'number_format', 'striptags', 'first', 'last', 'abs', 'join',
    'split',
];

const formConfigurations = new WeakMap();
const formInstances = new WeakMap();
const sampleTimers = new WeakMap();
const sampleIds = new WeakMap();
const initializedSampleInputs = new WeakSet();

const SAMPLE_FETCH_DEBOUNCE_MS = 300;

const sampleStorageKey = (storeId, kind) => kind === 'product'
    ? `automations:sampleProductSku:${storeId}`
    : `automations:sampleOrderDisplayId:${storeId}`;

const storedSampleId = (storeId, kind) => {
    try {
        return localStorage.getItem(sampleStorageKey(storeId, kind)) ?? '';
    } catch {
        return '';
    }
};

const storeSampleId = (storeId, kind, value) => {
    try {
        if (value === '') {
            localStorage.removeItem(sampleStorageKey(storeId, kind));
        } else {
            localStorage.setItem(sampleStorageKey(storeId, kind), value);
        }
    } catch {
        // Private-mode storage failures just lose persistence.
    }
};

const configurationFor = form => {
    if (!formConfigurations.has(form)) {
        formConfigurations.set(form, {
            shape:                JSON.parse(form.dataset.templateContext),
            labels:               JSON.parse(form.dataset.twigLabels),
            integrationFieldUrls: JSON.parse(form.dataset.integrationFieldUrls ?? '{}'),
        });
    }

    return formConfigurations.get(form);
};

const applyShape = (form, shape) => {
    configurationFor(form).shape = shape;

    const registered = formInstances.get(form) ?? [];

    for (const entry of registered) {
        // Operator/scope swaps detach old value fields; drop them so we stop
        // writing to dead instances and stop pinning their DOM from GC.
        if (!entry.instance.wrapper?.isConnected) {
            registered.delete(entry);

            continue;
        }

        entry.instance.data           = shape.context;
        entry.instance.fieldTypes     = shape.types;
        entry.instance.dateTimeFormat = entry.isActionField ? shape.dateTimeFormat : null;
    }

    form.dispatchEvent(new CustomEvent('templatefields:shape', {bubbles: true}));
};

// The sample-entity input lives in the panel header, outside the form.
const sampleInputForm = input =>
    input.closest('.side-panel').querySelector('form[data-template-context]');

const refreshSampleShape = async (input, sampleId) => {
    const form = sampleInputForm(input);

    if (sampleIds.get(form) === sampleId) {
        return;
    }

    sampleIds.set(form, sampleId);

    if (sampleId === '') {
        toggleLoading(input.parentElement, false);
        applyShape(form, JSON.parse(form.dataset.templateContext));

        return;
    }

    const url   = form.dataset.editorContextUrl.replace('SAMPLE_ID', encodeURIComponent(sampleId));
    toggleLoading(input.parentElement, true);

    const shape = await fetchJson(url);

    if (sampleIds.get(form) !== sampleId) {
        return;
    }

    toggleLoading(input.parentElement, false);

    if (!shape) {
        applyShape(form, JSON.parse(form.dataset.templateContext));

        return;
    }

    applyShape(form, shape);
};

const previewTimers = new WeakMap();

// Renders a templated field value against the loaded sample entity; failed or
// inapplicable renders leave the caller's raw presentation in place.
export const renderSamplePreview = (field, apply) => {
    const form = field.closest('form[data-template-context]');

    if (!form || !form.dataset.editorPreviewUrl) {
        return;
    }

    const sampleId = sampleIds.get(form) ?? '';
    const template = field.value;

    if (sampleId === '' || !/{{|{%/.test(template)) {
        return;
    }

    const operator = field.closest('.condition')?.querySelector('[name="operator"]')?.value ?? '';

    clearTimeout(previewTimers.get(field));
    previewTimers.set(field, setTimeout(async () => {
        const kind    = field.closest('.automation-action') ? 'action' : 'filter';
        const idParam = form.dataset.sampleIdParam ?? 'displayId';
        const url     = form.dataset.editorPreviewUrl
            + '&' + idParam + '=' + encodeURIComponent(sampleId)
            + '&kind=' + kind
            + '&template=' + encodeURIComponent(template)
            + (operator ? '&operator=' + encodeURIComponent(operator) : '');

        const payload = await fetchJson(url);

        if (!payload || field.value !== template || sampleIds.get(form) !== sampleId) {
            return;
        }

        const {output, invalid} = payload;

        if (output !== null) {
            apply(output, invalid === true);
        }
    }, SAMPLE_FETCH_DEBOUNCE_MS));
};

const attach = input => {
    const form            = input.closest('form[data-template-context]');
    const {shape, labels, integrationFieldUrls} = configurationFor(form);

    // Filter values use the tag-less FilterValueRenderer sandbox and compare
    // against raw order data, so: no `{%` flows, no display |date() format.
    const isActionField = input.closest('.automation-action') !== null;

    const instance = TwigBuilder.attach(input, {
        data:             shape.context,
        labels,
        integrationFieldUrls,
        filters:          isActionField ? ACTION_VALUE_FILTERS : FILTER_VALUE_FILTERS,
        addButtonLabel:   '{{',
        openMenuOnBraces: true,
        allowTags:        isActionField,
        fieldTypes:       shape.types,
        dateTimeFormat:   isActionField ? shape.dateTimeFormat : null,
    });

    if (instance) {
        if (!formInstances.has(form)) {
            formInstances.set(form, new Set());
        }

        formInstances.get(form).add({instance, isActionField});
    }
};

export const growTemplateField = textarea => {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight + 2}px`;
};

// A swapped-in fragment can carry a replacement template context for its
// owning form (e.g. the create-workflow filter section after an event change).
const syncSwappedTemplateContext = scope => {
    const carriers = [scope, ...scope.querySelectorAll('[data-template-context]')].filter(
        element => element.matches?.('[data-template-context]') && element.tagName !== 'FORM',
    );

    for (const carrier of carriers) {
        const form = carrier.closest('form[data-template-context]');

        if (!form || form.dataset.templateContext === carrier.dataset.templateContext) {
            continue;
        }

        form.dataset.templateContext = carrier.dataset.templateContext;
        applyShape(form, JSON.parse(carrier.dataset.templateContext));
    }
};

export const initializeTemplateFields = scope => {
    syncSwappedTemplateContext(scope);

    scope.querySelectorAll('input[data-sample-input]').forEach(input => {
        if (initializedSampleInputs.has(input)) {
            return;
        }

        initializedSampleInputs.add(input);

        const storeId = input.dataset.storeId;
        const kind    = input.dataset.sampleKind ?? 'order';

        // The preview input is not automation data: keep its events away from
        // the dirty-form tracker and every other delegated listener.
        input.addEventListener('input', event => {
            event.stopPropagation();

            const form = sampleInputForm(input);

            storeSampleId(storeId, kind, input.value.trim());

            clearTimeout(sampleTimers.get(form));
            sampleTimers.set(form, setTimeout(
                () => refreshSampleShape(input, input.value.trim()),
                SAMPLE_FETCH_DEBOUNCE_MS,
            ));
        });

        const stored = storedSampleId(storeId, kind);

        if (stored !== '' && input.value === '') {
            input.value = stored;
            refreshSampleShape(input, stored);
        }
    });

    scope.querySelectorAll('input[data-templatable], textarea[data-templatable]').forEach(field => {
        if (field instanceof HTMLTextAreaElement) {
            growTemplateField(field);
        }

        if (!TwigBuilder.get(field)) {
            attach(field);
        }
    });
};

// Catch-all for fields cloned into the DOM by paths without an init call.
// Attaching re-parents the field into the highlight wrapper, which drops
// focus — restore it along with the caret.
export const attachTemplateFieldOnFocus = field => {
    if (!field.matches('input[data-templatable], textarea[data-templatable]') || TwigBuilder.get(field)) {
        return;
    }

    const {selectionStart, selectionEnd} = field;

    attach(field);

    field.focus();
    field.setSelectionRange(selectionStart, selectionEnd);
};
